import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './../retail.scss';
import retailSquare from './../../assets/images/retail-shapes-square.svg';
import retailCircle from './../../assets/images/retail-shapes-circle.svg';
import retailTriangle from './../../assets/images/retail-shapes-triangle.svg';
import barChart from './../../assets/images/bar-chart.svg';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function RetailUsage() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(squareRef.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="retail-future">

        <p id="s1" className="intro" data-aos="fade-up">Explore the data to discover how businesses in the retail industry plan to use direct mail going forward.&#8288;<a href="#reference-1" class="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Motivators for More Mail Use*</h3>

                <p className="pull-quote no-margin-bottom"><span data-aos className='highlighter'>Higher return on ad spend (ROAS)</span></p>
                <p>Retail</p>

                <ul className="no-icons">
                  <li>Financial — Ability to track campaign from print to delivery</li>
                  <li>Health — Easier audience targeting</li>
                  <li>Insurance — Easier personalization</li>
                </ul>

                <p className="disclaimer">*Consumer packaged goods companies had too low a sample size to be included in this part of the survey.</p>

                <p>More than businesses in any other industry, retail companies reported that achieving a higher return on advertising spend would be the main motivator to increasing direct mail usage going forward.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>To ensure you get the most out of your campaigns, create mailpieces that command attention. Clear, easy-to-read messaging and a clean, eye-catching design can entice customers to take a closer look.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>

                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Impediments to Mail Use</h3>
                    <div className='progressbar-item'>
                      <span className='label'>Cost per mailing/too expensive</span>
                      <div className='progress-bar size-small' data-aos data-aos-id="bar-anim">
                        <div className='progress' data-fill="35"><span class="percentage" data-to="35" aria-hidden="true">0</span><span class="screen-reader-text">35</span>%</div>
                      </div>
                    </div>

                    <p className='margin-top-30'>Across all industries, 35% of businesses said that cost per mailing, and direct mail being too expensive generally, are the main impediments to using mail. Retail companies also cite this as the main reason for not using direct mail more often.</p>
                    
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p id="s2">Because direct mail involves some tangible, upfront costs for paper, printing and so on, some marketers believe direct mail may be too expensive for them. Yet nearly seven out of 10 marketers feel direct mail is the highest ROI marketing channel at their disposal.&#8288;<a href="#reference-2" className="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a></p>
                  </div>
                </div>
              </div>

              <div className='inner-col-item'>
                <div className='personalized-box align-to-text'>
                  <h3>Most retail businesses plan to continue using mail in the future.</h3>
                  <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={retailTriangle} alt="" />
                    </div>
                    <div className='square'>
                      <img ref={squareRef} src={retailSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={retailTriangle} alt="" />
                    </div>
                    <div className='circle'>
                      <img src={retailCircle} alt="" />
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Using Direct Mail in the Future</h3>

                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="91" aria-hidden="true">0</span><span class="screen-reader-text">91</span>%</span>
                </span>

                <p>More or the same</p>

                <p>Ninety-one percent of retail businesses said they plan to use direct mail the same amount or more in future campaigns.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The future of direct mail is rife with opportunity. With <a href="/../evolution-of-a-medium/">advanced tech and digital innovations</a>, it’s easier than ever for brands to create memorable experiences. From augmented and virtual reality to near-field communication and QR Codes, there are many options to choose from to meet your specific business goals and needs.</p>
              </div>
            </div>
            
            
          </div>
        </div>



      </div>
    </>
  );
}
